import React, { ReactNode, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import Button from './Button';

export interface ConfirmDialogHandle {
  show: (options: {
    title: string;
    message: ReactNode;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info' | 'success';
    input?: { type: string; defaultValue?: string; placeholder?: string };
  }) => Promise<{ value?: string } | null>;
}

interface ConfirmDialogProps {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  isLoading?: boolean;
}

interface InternalState {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  confirmText: string;
  cancelText: string;
  type: 'danger' | 'warning' | 'info' | 'success';
  input?: { type: string; defaultValue?: string; placeholder?: string };
}

const ConfirmDialog = forwardRef<ConfirmDialogHandle, ConfirmDialogProps>((props, ref) => {
  const [impState, setImpState] = useState<InternalState>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    type: 'warning',
  });
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const resolverRef = useRef<((value: { value?: string } | null) => void) | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    show: (options) => {
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setInputValue(options.input?.defaultValue ?? '');
        setImpState({
          isOpen: true,
          title: options.title,
          message: options.message,
          confirmText: options.confirmText ?? 'Confirmer',
          cancelText: options.cancelText ?? 'Annuler',
          type: options.type ?? 'warning',
          input: options.input,
        });
      });
    },
  }), []);

  const effective = impState.isOpen
    ? impState
    : {
        isOpen: !!props.isOpen,
        title: props.title ?? '',
        message: props.message,
        confirmText: props.confirmText ?? 'Confirmer',
        cancelText: props.cancelText ?? 'Annuler',
        type: props.type ?? 'warning' as const,
        input: undefined,
      };

  if (!effective.isOpen) {
    return null;
  }

  const getIcon = () => {
    switch (effective.type) {
      case 'danger':
        return <AlertTriangle className="w-10 h-10 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-10 h-10 text-yellow-600" />;
      case 'success':
        return <CheckCircle2 className="w-10 h-10 text-green-600" />;
      case 'info':
      default:
        return <Info className="w-10 h-10 text-blue-600" />;
    }
  };

  const getButtonColor = () => {
    switch (effective.type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
      case 'success':
        return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
      case 'info':
      default:
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  const handleConfirm = () => {
    if (impState.isOpen) {
      resolverRef.current?.({ value: inputValue });
      resolverRef.current = undefined;
      setImpState((s) => ({ ...s, isOpen: false }));
    } else {
      props.onConfirm?.();
    }
  };

  const handleCancel = () => {
    if (impState.isOpen) {
      resolverRef.current?.(null);
      resolverRef.current = undefined;
      setImpState((s) => ({ ...s, isOpen: false }));
    } else {
      props.onClose?.();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCancel}></div>

        {/* Dialog */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-bleu rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 bg-bleu sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-opacity-20 rounded-full sm:mx-0 sm:h-10 sm:w-10"
                style={{ backgroundColor: `rgba(var(--${effective.type}-color-rgb), 0.2)` }}>
                {getIcon()}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-gray-900">{effective.title}</h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{effective.message}</p>
                  {effective.input && (
                    <input
                      type={effective.input.type}
                      defaultValue={effective.input.defaultValue}
                      placeholder={effective.input.placeholder}
                      onChange={(e) => setInputValue(e.target.value)}
                      className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              onClick={handleConfirm}
              className={getButtonColor()}
              isLoading={isLoading || props.isLoading}
            >
              {effective.confirmText}
            </Button>
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="mr-2"
              disabled={isLoading || props.isLoading}
            >
              {effective.cancelText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

ConfirmDialog.displayName = 'ConfirmDialog';

export default ConfirmDialog;
